import {useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import {Collection} from '@tinct/web-collection';
import {LayoutContent, LayoutTopbar, useBreadcrumb} from '@tinct/web-layout';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  MetricCard,
} from '@tinct/web-components';
import {useGetAudienceById, useGetAudienceLeads} from '@tinct/core-api';
import {useCurrentWorkspaceUrlPrefix} from '@tinct/core-workspace';
import {useAudienceLeadsColumns} from './hooks/use-audience-leads-columns';
import {IconBuilding, IconDots, IconPencil, IconPlus, IconTrash} from '@tabler/icons-react';
import {CreateAudienceModal} from '../../modals/create-audience-modal/create-audience-modal';
import {DeleteAudienceModal} from '../../modals/delete-audience-modal/delete-audience-modal';
import {RenameAudienceModal} from '../../modals/rename-audience-modal/rename-audience-modal';
import {
  RemoveLeadFromAudienceModal
} from '../../modals/remove-lead-from-audience-modal/remove-lead-from-audience-modal';
import {ReadLead} from '@tinct/core-api-models';
import {useQueryClient} from '@tanstack/react-query';
import {Building2, Megaphone} from "lucide-react";

export function EditAudiencePage() {
  const { audienceId } = useParams<{ audienceId: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const workspaceBaseUrl = useCurrentWorkspaceUrlPrefix();
  const [isAddCompaniesModalOpen, setIsAddCompaniesModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isRemoveLeadModalOpen, setIsRemoveLeadModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<ReadLead | null>(null);

  const columns = useAudienceLeadsColumns({
    onRemoveLead: (lead) => {
      setSelectedLead(lead);
      setIsRemoveLeadModalOpen(true);
    },
  });

  const { data: audience, isLoading: isLoadingAudience } = useGetAudienceById(
    {
      id: audienceId ?? '',
      fields: {
        id: null,
        name: null,
        campaigns: {
          fields: {
            id: null,
            name: null,
          },
        },
        statistics: {
          fields: {
            leadsCount: null,
            campaignsCount: null,
          },
        },
        createdAt: null,
      },
    },
    {
      enabled: !!audienceId,
    }
  );

  const breadcrumbs = useBreadcrumb([
    { label: 'Dashboard', to: `${workspaceBaseUrl}/dashboard` },
    { label: 'Audiences', to: `${workspaceBaseUrl}/audiences` },
  ], true);

  const formatDate = (date: string | null | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB');
  };

  return (
    <>
      <header className="sticky top-0 z-30">
        <LayoutTopbar
          breadcrumbs={breadcrumbs}
        />
      </header>
      <div className="min-h-[calc(100vh-4rem)]">
        <div className={"bg-muted"}>
          <LayoutContent >
            {/* Header */}
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-semibold tracking-tight">
                {isLoadingAudience ? (
                  <span className="inline-block h-9 w-64 animate-pulse rounded bg-muted" />
                ) : (
                  audience?.name ?? 'Audience'
                )}
              </h1>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <IconDots className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setIsRenameModalOpen(true)}>
                    <IconPencil className="size-4" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive" onClick={() => setIsDeleteModalOpen(true)}>
                    <IconTrash className="size-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Metrics */}
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
              <MetricCard
                headerText="Targets"
                icon={<Building2 className="size-4 text-muted-foreground" />}
                statisticText={`${audience?.statistics?.leadsCount ?? 0} companies`}
                descriptionText={`Created on ${formatDate(audience?.createdAt)}`}
                variant={isLoadingAudience ? 'empty' : 'default'}
              />
              <MetricCard
                headerText="Used in"
                icon={<Megaphone className="size-4 text-muted-foreground" />}
                statisticText={`${audience?.statistics?.campaignsCount ?? 0} campaigns`}
                descriptionText="Campaigns using this audience"
                variant={isLoadingAudience ? 'empty' : 'default'}
              />
            </div>
          </LayoutContent>
        </div>
        <LayoutContent>
          {/* Companies Table */}
          <div className="mt-8">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold font-heading">Companies</h2>
              <Button onClick={() => setIsAddCompaniesModalOpen(true)}>
                <IconPlus className="size-4" />
                Add companies
              </Button>
            </div>
            {audience && (
              <RenameAudienceModal
                audience={audience}
                open={isRenameModalOpen}
                onOpenChange={setIsRenameModalOpen}
                onRenamed={() => {
                  queryClient.invalidateQueries({ queryKey: ['audience', audienceId] });
                }}
              />
            )}
            {audience && (
              <DeleteAudienceModal
                audience={audience}
                open={isDeleteModalOpen}
                onOpenChange={setIsDeleteModalOpen}
                onDeleted={() => navigate('..')}
              />
            )}
            {audience && selectedLead && (
              <RemoveLeadFromAudienceModal
                lead={selectedLead}
                audience={audience}
                open={isRemoveLeadModalOpen}
                onOpenChange={setIsRemoveLeadModalOpen}
                onRemoved={() => {
                  queryClient.invalidateQueries({ queryKey: ['audiences', audienceId, 'leads'] });
                  queryClient.invalidateQueries({ queryKey: ['audience', audienceId] });
                }}
              />
            )}
            {audience && (
              <CreateAudienceModal
                open={isAddCompaniesModalOpen}
                onOpenChange={setIsAddCompaniesModalOpen}
                existingAudience={audience}
                campaignBaseUrl={`${workspaceBaseUrl}/campaigns`}
                completedUrl={`${workspaceBaseUrl}/audiences/${audienceId}`}
              />
            )}
            <Collection
              useLocationAsState
              columns={columns}
              filtersConfiguration={{
                textFilter: {
                  placeholder: 'Search',
                  queryParamName: 'term',
                },
              }}
              emptyState={
                <div className="flex flex-col items-center gap-6 py-8 px-16">
                  <div className="flex flex-col items-center gap-4">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
                      <IconBuilding className="size-6" />
                    </div>
                    <div className="flex flex-col items-center gap-2 text-center">
                      <p className="text-lg font-medium">No company yet</p>
                      <p className="text-sm text-muted-foreground">Add your first company</p>
                    </div>
                  </div>
                  <Button onClick={() => setIsAddCompaniesModalOpen(true)}>
                    <IconPlus className="size-4" />
                    Add companies
                  </Button>
                </div>
              }
              useQueryFn={useGetAudienceLeads}
              staticParams={{
                audienceId: audienceId ?? '',
              }}
              queryFields={{
                id: null,
                company: {
                  fields: {
                    id: null,
                    name: null,
                    domain: null,
                    industry: null,
                    enrichmentScore: null,
                    logo: {
                      fields: {
                        url: null,
                      },
                    },
                  },
                },
                tag: null,
                audiences: {
                  fields: {
                    id: null,
                    name: null,
                  },
                },
                pages: {
                  fields: {
                    id: null,
                    status: null,
                  },
                },
              }}
            />
          </div>
        </LayoutContent>
      </div>
    </>
  );
}
